import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Badge, Button, FormError, PageHead, TextField, useToast } from "../../components/ui";
import { formatDateTime, ROLE_LABEL } from "../../lib/format";
import { t } from "../../i18n";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

export function AccountPage() {
  const { me, logout } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [sending, setSending] = useState(false);
  if (!me) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast(t("Senha alterada. Entre novamente com a nova senha."));
      await logout();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setSending(true);
    try {
      const res = await api.post<{ message: string }>("/auth/resend-verification");
      toast(res.message);
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageHead title={t("Minha conta")} />
      <div className="split">
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Dados de acesso")}</h2>
          </div>
          <div className="panel-body stack">
            <dl className="facts">
              <dt>{t("Nome")}</dt>
              <dd>{me.full_name}</dd>
              <dt>{t("E-mail")}</dt>
              <dd>
                {me.email} {me.email_verified ? <Badge tone="ok">{t("Confirmado")}</Badge> : <Badge tone="warn">{t("Não confirmado")}</Badge>}
              </dd>
              <dt>{t("Fuso horário")}</dt>
              <dd>{me.timezone}</dd>
              <dt>{t("Último acesso")}</dt>
              <dd>{formatDateTime(me.last_login_at)}</dd>
              <dt>{t("Vínculos")}</dt>
              <dd>
                {me.is_platform_admin && <div>{t("Administração da plataforma")}</div>}
                {me.organizations.map((o) => (
                  <div key={o.organization_id}>
                    {o.organization_name}: {o.roles.map((r) => ROLE_LABEL[r]).join(", ")}
                  </div>
                ))}
              </dd>
            </dl>
            {!me.email_verified && (
              <div>
                <Button onClick={resend} busy={sending}>
                  {t("Reenviar confirmação de e-mail")}
                </Button>
              </div>
            )}
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Idioma")}</h2>
          </div>
          <div className="panel-body stack">
            <p className="muted">
              {t("O idioma da interface, dos avisos e dos e-mails que você recebe. Os manuais da Ajuda estão disponíveis só em português.")}
            </p>
            <LanguageSwitcher loggedIn />
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Trocar senha")}</h2>
          </div>
          <div className="panel-body">
            <form className="form" onSubmit={submit} noValidate>
              {error && <FormError error={error} />}
              <TextField label={t("Senha atual")} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
              <TextField label={t("Nova senha")} type="password" autoComplete="new-password" hint={t("Mínimo de 10 caracteres, com letras e números.")}
                value={next} onChange={(e) => setNext(e.target.value)} />
              <TextField label={t("Confirme a nova senha")} type="password" autoComplete="new-password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} error={mismatch ? t("As senhas não conferem.") : undefined} />
              <p className="muted">{t("Ao trocar a senha, todas as suas sessões são encerradas.")}</p>
              <div>
                <Button type="submit" variant="primary" busy={busy}>
                  {t("Trocar senha")}
                </Button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}
